import { useRef, useState, useEffect, useCallback } from "react";

// ── AudioPlayer ───────────────────────────────────────────────────────────────
// Воспроизводит аудио с визуализатором (эквалайзер на canvas).
// Поддерживает обычный режим, reversed (через proxy) и fast (playbackRate).

export default function AudioPlayer({ audioUrl, coverUrl, revealed, tv, httpUrl, reverseProxyUrl, isReverse, playbackRate = 1, onAudioError }) {
  const audioRef    = useRef(null);
  const canvasRef   = useRef(null);
  const ctxRef      = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef   = useRef(null);
  const revSrcRef   = useRef(null);
  const rafRef      = useRef(null);
  const abortRef    = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(false);

  // Suppress unhandled audio decode errors so React dev overlay doesn't fire
  useEffect(() => {
    const suppress = (e) => {
      const msg = e?.reason?.message || e?.message || "";
      if (msg.includes("decode") || msg.includes("audio") || msg.includes("Unable to decode")) {
        e.preventDefault();
        e.stopPropagation?.();
      }
    };
    window.addEventListener("unhandledrejection", suppress);
    window.addEventListener("error", suppress);
    return () => {
      window.removeEventListener("unhandledrejection", suppress);
      window.removeEventListener("error", suppress);
    };
  }, []);

  const resolvedUrl = audioUrl
    ? (audioUrl.startsWith("http") ? audioUrl : (httpUrl + audioUrl))
    : "";

  // ── Equalizer draw loop ──────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas   = canvasRef.current;
    const analyser = analyserRef.current;
    if (!canvas || !analyser) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const data = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(data);
    ctx.clearRect(0, 0, W, H);
    const COUNT = tv ? 64 : 48;
    const BAR_W = W / COUNT - 1;
    const step  = Math.floor(data.length / COUNT);
    for (let i = 0; i < COUNT; i++) {
      let sum = 0;
      for (let j = 0; j < step; j++) sum += data[i * step + j];
      const val  = (sum / step) / 255;
      const barH = Math.max(2, val * H);
      const x    = i * (BAR_W + 1);
      const y    = H - barH;
      const r = Math.round(61  + val * 194);
      const g = Math.round(255 - val * 255);
      const b = Math.round(160 + val * 95);
      ctx.fillStyle = `rgb(${r},${g},${b})`;
      ctx.beginPath();
      const rad = Math.min(BAR_W / 2, 4);
      ctx.moveTo(x + rad, y);
      ctx.lineTo(x + BAR_W - rad, y);
      ctx.quadraticCurveTo(x + BAR_W, y, x + BAR_W, y + rad);
      ctx.lineTo(x + BAR_W, H);
      ctx.lineTo(x, H);
      ctx.lineTo(x, y + rad);
      ctx.quadraticCurveTo(x, y, x + rad, y);
      ctx.closePath();
      ctx.fill();
    }
    rafRef.current = requestAnimationFrame(draw);
  }, [tv]);

  const getCtx = () => {
    if (ctxRef.current && ctxRef.current.state !== "closed") return ctxRef.current;
    const WAC = window.AudioContext || window.webkitAudioContext;
    if (!WAC) return null;
    ctxRef.current = new WAC();
    return ctxRef.current;
  };

  const setupNormalAudio = () => {
    const ctx = getCtx();
    if (!ctx || !audioRef.current) return;
    if (sourceRef.current) {
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      return;
    }
    try {
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      const src = ctx.createMediaElementSource(audioRef.current);
      src.connect(analyser);
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;
      sourceRef.current   = src;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
    } catch(e) {
      console.warn("[Audio] setupNormal failed:", e);
    }
  };

  const playReversed = async (proxyUrl) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const fullUrl = proxyUrl.startsWith("http") ? proxyUrl : (httpUrl + proxyUrl);
      const resp = await fetch(fullUrl, { signal: controller.signal });
      if (!resp.ok) throw new Error(`proxy HTTP ${resp.status}`);
      const arrayBuf = await resp.arrayBuffer();
      if (controller.signal.aborted) return;

      if (ctxRef.current && !sourceRef.current) {
        try { ctxRef.current.close(); } catch(_) {}
        ctxRef.current = null;
        analyserRef.current = null;
      }
      const ctx = getCtx();
      if (!ctx) throw new Error("No AudioContext");
      if (ctx.state === "suspended") await ctx.resume();

      const buffer = await ctx.decodeAudioData(arrayBuf);
      if (controller.signal.aborted) return;

      for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
        buffer.getChannelData(ch).reverse();
      }

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      analyser.connect(ctx.destination);
      analyserRef.current = analyser;

      try { revSrcRef.current?.stop(); } catch(_) {}

      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.connect(analyser);
      src.onended = () => { setPlaying(false); cancelAnimationFrame(rafRef.current); };
      src.start(0);
      revSrcRef.current = src;

      setPlaying(true);
      setLoading(false);
      cancelAnimationFrame(rafRef.current);
      draw();
    } catch(e) {
      if (e.name === "AbortError") return;
      console.error("[ReverseAudio]", e.message || e);
      setError(true);
      setLoading(false);
      if (onAudioError) onAudioError();
    }
  };

  // React to URL / mode changes
  useEffect(() => {
    setError(false);
    setPlaying(false);
    cancelAnimationFrame(rafRef.current);

    if (isReverse) {
      try { audioRef.current?.pause(); } catch(_) {}
      if (!reverseProxyUrl) return;
      setLoading(true);
      playReversed(reverseProxyUrl);
    } else {
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      try { revSrcRef.current?.stop(); } catch(_) {}

      const el = audioRef.current;
      if (!el || !resolvedUrl) { setLoading(false); return; }

      setLoading(true);
      el.pause();
      el.crossOrigin = "anonymous";
      el.src = resolvedUrl;
      el.load();
      el.playbackRate = playbackRate;
      el.play()
        .then(() => {
          setPlaying(true);
          setLoading(false);
          setupNormalAudio();
          cancelAnimationFrame(rafRef.current);
          draw();
        })
        .catch(e => {
          if (e.name !== "AbortError") {
            setError(true);
            setLoading(false);
            if (onAudioError) onAudioError();
          }
        });
    }
    return () => { cancelAnimationFrame(rafRef.current); };
  }, [resolvedUrl, reverseProxyUrl, isReverse]); // eslint-disable-line

  // Stop on reveal
  useEffect(() => {
    if (!revealed) return;
    try { audioRef.current?.pause(); } catch(_) {}
    try { revSrcRef.current?.stop(); } catch(_) {}
    setPlaying(false);
    cancelAnimationFrame(rafRef.current);
  }, [revealed]);

  const SIZE = tv ? 280 : 200;

  return (
    <div style={{
      width: "100%",
      background: "linear-gradient(135deg,#0d0520 0%,#1a0a3d 50%,#0a1a2d 100%)",
      border: "2.5px solid #9b5de544",
      borderRadius: "1.5rem",
      boxShadow: "6px 6px 0 #000, 0 0 40px rgba(155,93,229,0.15)",
      padding: tv ? "1.5rem" : "1rem",
      display: "flex",
      flexDirection: tv ? "row" : "column",
      alignItems: "center",
      gap: tv ? "1.5rem" : "1rem",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Ambient glow */}
      {playing && !revealed && (
        <div style={{
          position: "absolute", inset: 0, zIndex: 0,
          background: "radial-gradient(ellipse at 30% 50%, rgba(155,93,229,0.12) 0%, transparent 70%)",
          pointerEvents: "none",
        }} />
      )}

      {/* Album cover */}
      <div style={{
        width: SIZE, height: SIZE, flexShrink: 0,
        borderRadius: "1rem",
        border: "3px solid #000",
        boxShadow: playing && !revealed ? "4px 4px 0 #000, 0 0 30px rgba(155,93,229,0.5)" : "4px 4px 0 #000",
        overflow: "hidden", position: "relative", zIndex: 1,
        animation: playing && !revealed ? "spin-slow 8s linear infinite" : "none",
      }}>
        {coverUrl && !revealed ? (
          <>
            <img src={coverUrl} alt="" style={{
              width: "100%", height: "100%", objectFit: "cover",
              filter: "blur(18px) brightness(0.6)", transform: "scale(1.15)",
            }} />
            <div style={{
              position: "absolute", inset: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: tv ? "5rem" : "3.5rem",
              animation: playing ? "float 2s ease-in-out infinite" : "none",
            }}>{isReverse ? "🔁" : "🎵"}</div>
          </>
        ) : revealed && coverUrl ? (
          <img src={coverUrl} alt="Обложка" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        ) : (
          <div style={{
            width: "100%", height: "100%",
            background: "linear-gradient(135deg,#1e0a3d,#0d0820)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: tv ? "5rem" : "3.5rem",
            animation: playing && !revealed ? "float 2s ease-in-out infinite" : "none",
          }}>
            {error ? "⚠️" : loading ? "⏳" : isReverse ? "🔁" : "🎵"}
          </div>
        )}
      </div>

      {/* Equalizer + status */}
      <div style={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", gap: "0.5rem", zIndex: 1 }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: "0.5rem",
          background: error ? "#1a0010" : playing && !revealed ? "#001a10" : "#0d0520",
          border: `1.5px solid ${error ? "#ff335544" : playing && !revealed ? "#3dffa055" : "#9b5de533"}`,
          borderRadius: "9999px", padding: "0.3rem 0.85rem",
          alignSelf: "flex-start",
        }}>
          <div style={{
            width: "8px", height: "8px", borderRadius: "50%",
            background: error ? "#ff3355" : playing && !revealed ? "#3dffa0" : "#9b5de5",
            boxShadow: playing && !revealed ? "0 0 8px #3dffa0" : "none",
            animation: playing && !revealed ? "pulse-dot 1s ease-in-out infinite" : "none",
          }} />
          <span style={{
            fontFamily: "'Nunito',sans-serif", fontWeight: 700,
            fontSize: tv ? "0.95rem" : "0.8rem",
            color: error ? "#ff3355" : playing && !revealed ? "#3dffa0" : "#9b5de5",
          }}>
            {error ? "Ошибка загрузки" : loading ? "Загрузка..." : playing && !revealed
              ? (isReverse ? "🔁 Слушайте наоборот!" : "♪ Слушайте...")
              : revealed ? "Ответ раскрыт" : "Готово"}
          </span>
        </div>

        <canvas ref={canvasRef}
          width={tv ? 500 : 320} height={tv ? 100 : 70}
          style={{
            width: "100%", height: tv ? "100px" : "70px",
            borderRadius: "0.75rem", background: "rgba(0,0,0,0.3)",
            opacity: playing && !revealed ? 1 : 0.15,
            transition: "opacity 0.5s",
          }}
        />
      </div>

      <audio ref={audioRef}
        crossOrigin="anonymous"
        onEnded={() => { setPlaying(false); cancelAnimationFrame(rafRef.current); }}
        onError={(e) => {
          if (e.target?.error?.code === 1) return;
          setError(true); setLoading(false);
        }}
        onPlay={() => setPlaying(true)}
        style={{ display: "none" }}
      />

      <style>{`
        @keyframes spin-slow { to { transform: rotate(360deg); } }
        @keyframes pulse-dot {
          0%,100% { opacity:1; transform:scale(1); }
          50%      { opacity:0.5; transform:scale(0.8); }
        }
      `}</style>
    </div>
  );
}